import { useState } from "react";
import TaskCard from "@/components/TaskCard";
import api from "@/api/client";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { STATUS_META, STATUS_ORDER as _SO } from "@/constants/status";

const COLUMNS = _SO.map((k) => ({ key: k, label: STATUS_META[k].label, accent: "text-white", dot: STATUS_META[k].dot }));

export default function KanbanView({
  tasks, users, isManager, currentUserId,
  onEdit, onDelete, onTogglePriority, onTasksUpdated, onStatusChange,
  leavesByUser = {}, todayIso = "",
}) {
  const { user } = useAuth();
  const [dragOver, setDragOver] = useState(null);
  const [holdPrompt, setHoldPrompt] = useState(null); // { task, newStatus }

  // Bucket by the VIEWER'S personal status when the task is multi-assignee and
  // the current user is one of the assignees — this keeps the "Complete" badge
  // (which TaskCard also renders as personal status) in the same column instead
  // of showing a green-badged card in the "In Progress" column just because
  // another co-assignee hasn't finished yet.
  const bucketStatusFor = (t) => {
    const isMulti = (t.assignees || []).length > 1;
    if (isMulti && currentUserId) {
      const slot = (t.assignee_details || []).find((a) => a.user_id === currentUserId);
      if (slot && slot.status) return slot.status;
    }
    return t.status;
  };

  const byStatus = {};
  COLUMNS.forEach((c) => (byStatus[c.key] = []));
  tasks.forEach((t) => {
    const bucket = bucketStatusFor(t);
    if (!byStatus[bucket]) byStatus[bucket] = [];
    byStatus[bucket].push(t);
  });

  const handleDragStart = (e, task) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/task-id", task.id);
    e.dataTransfer.setData("text/current-status", task.status);
  };

  const performMove = async (taskId, newStatus, extraFields = {}) => {
    try {
      await api.patch(`/tasks/${taskId}`, { status: newStatus, ...extraFields });
      onTasksUpdated?.((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, status: newStatus, ...extraFields } : t))
      );
      toast.success(`“${tasks.find((x) => x.id === taskId)?.title || "Task"}” → ${STATUS_META[newStatus]?.label || newStatus}`);
    } catch (err) {
      if (err?.response?.data?.detail?.code === "TIME_REQUIRED") toast.info("Not completed — time spent is required");
      else toast.error("Move failed");
    }
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    setDragOver(null);
    const taskId = e.dataTransfer.getData("text/task-id");
    const current = e.dataTransfer.getData("text/current-status");
    if (!taskId || current === newStatus) return;

    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;

    if (task.assigned_to !== currentUserId && !isManager) {
      toast.error("Only the assignee or their manager can move this task.");
      return;
    }

    if (newStatus === "on_hold") {
      // Hold reason is OPTIONAL — flip directly, no modal.
      await performMove(taskId, newStatus, { hold_reason: task.hold_reason || "" });
      return;
    }
    if (newStatus === "incomplete") {
      const reason = window.prompt(`Mark "${task.title}" as INCOMPLETE. Reason?`);
      if (!reason) return;
      await performMove(taskId, newStatus, { incomplete_reason: reason });
      return;
    }
    await performMove(taskId, newStatus);
  };

  const submitHold = async (e) => {
    // Retained for API compatibility even though the modal is no longer
    // auto-opened on drag — a manager can still open it from the pencil
    // icon on any on-hold card to add / edit an optional reason.
    e.preventDefault();
    const reason = e.currentTarget.reason.value.trim();
    const { task } = holdPrompt;
    await performMove(task.id, "on_hold", { hold_reason: reason });
    setHoldPrompt(null);
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
        {COLUMNS.map((col) => (
          <div
            key={col.key}
            className={`kbcolumn p-3 min-h-[400px] transition-colors ${dragOver === col.key ? "kbcolumn-drop" : ""}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(col.key); }}
            onDragLeave={() => setDragOver((cur) => (cur === col.key ? null : cur))}
            onDrop={(e) => handleDrop(e, col.key)}
            data-testid={`kanban-col-${col.key}`}
          >
            <div className="flex items-center justify-between px-1 pb-3 mb-3 border-b border-white/8">
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${col.dot}`} />
                <div className={`text-sm font-semibold ${col.accent}`}>{col.label}</div>
                <span className="text-[0.7rem] font-mono text-white/40">{byStatus[col.key].length}</span>
              </div>
            </div>

            <div className="space-y-3">
              {byStatus[col.key].map((t) => (
                <div
                  key={t.id}
                  draggable={isManager || t.assigned_to === currentUserId}
                  onDragStart={(e) => handleDragStart(e, t)}
                  data-testid={`kanban-task-${t.id}`}
                  className="cursor-grab active:cursor-grabbing"
                >
                  <TaskCard
                    task={t}
                    currentUser={user}
                    users={users}
                    isManager={isManager}
                    onEdit={onEdit}
                    onDelete={isManager ? onDelete : null}
                    onTogglePriority={isManager ? onTogglePriority : null}
                    onStatusChange={onStatusChange}
                    assigneeOnLeaveToday={!!(todayIso && (leavesByUser[t.assigned_to] || []).includes(todayIso))}
                  />
                </div>
              ))}
              {byStatus[col.key].length === 0 && (
                <div className="text-xs text-white/50 p-4 border border-dashed border-white/15 rounded-md text-center">
                  No tasks here yet — drag a card in
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {holdPrompt && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/50 backdrop-blur-[2px] p-4"
             onClick={() => setHoldPrompt(null)}>
          <form
            onSubmit={submitHold}
            onClick={(e) => e.stopPropagation()}
            className="bg-surface border border-white/10 rounded-xl p-6 w-full max-w-md space-y-4"
            data-testid="hold-reason-dialog"
          >
            <div>
              <div className="overline mb-1 text-cyan-300">Put on hold</div>
              <div className="font-display text-xl tracking-tight">{holdPrompt.task.title}</div>
              <div className="text-xs text-white/50 mt-1">
                Add a reason. It stays visible on the card until you move it again.
              </div>
            </div>
            <textarea
              name="reason"
              autoFocus
              required
              data-testid="hold-reason-input"
              className="w-full bg-black/40 border border-white/10 rounded-md p-3 text-sm min-h-[80px] focus:outline-none focus:ring-1 focus:ring-cyan-500"
              placeholder=""
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setHoldPrompt(null)}
                className="px-4 py-2 text-sm text-white/60 hover:text-white"
              >Cancel</button>
              <button
                type="submit"
                data-testid="hold-reason-submit"
                className="px-5 py-2 text-sm rounded-full bg-cyan-500 hover:bg-cyan-400 text-white"
              >Put on hold</button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
